import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    doc, getDoc, collection, addDoc, onSnapshot, query, where, getDocs
} from 'firebase/firestore';
import { YouTubeEmbed } from '../YouTubeSection';
import './Abonamente.css';


const STRIPE_PRICES = {
    'starter': 'price_1SmeA4J9OHbEhYKcZEOrzz6c',
    'pro': 'price_1SmeBBJ9OHbEhYKcydRpTUVt',
    // 'transform' nu are nevoie de ID aici, fiindcă e prin aplicare
};

const PLAN_NAMES = {
    'starter': 'Fit Simplu Starter',
    'pro': 'Fit Simplu Pro',
    'transform': 'Fit Simplu Transformation'
};


const TRANSFORM_FORM_URL = "https://form.typeform.com/to/btCpmnQU";

function Abonamente() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [dietBlocks, setDietBlocks] = useState([]);
    const [workoutBlocks, setWorkoutBlocks] = useState([]);
    const [hasActiveSub, setHasActiveSub] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchPlanContent = async (uid) => {
        try {
            const planRef = doc(db, "users", uid, "plans", "current_plan");
            const planSnap = await getDoc(planRef);
            if (planSnap.exists()) {
                const planData = planSnap.data();
                setDietBlocks(planData.dietBlocks || []);
                setWorkoutBlocks(planData.workoutBlocks || []);
            }
        } catch (error) { console.error("Err plan:", error); }
    };

    const checkStripeSubscription = useCallback(async (uid) => {
        setLoading(true);
        try {
            const userRef = doc(db, "users", uid);
            const userSnap = await getDoc(userRef);
            let isTransformActive = false;

            if (userSnap.exists()) {
                const data = userSnap.data();
                setUserData(data);

                // Verificăm dacă adminul a activat manual Transformarea
                if (data.subscriptionPlan === 'transform' && data.subscriptionStatus === 'active') {
                    isTransformActive = true;
                }
            }

            // Verificăm abonamentele standard Stripe
            const subsRef = collection(db, "customers", uid, "subscriptions");
            const q = query(subsRef, where("status", "in", ["active", "trialing"]));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty || isTransformActive) {
                setHasActiveSub(true);
                await fetchPlanContent(uid);
            } else {
                setHasActiveSub(false);
            }

        } catch (error) {
            console.error("Eroare verificare:", error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                await checkStripeSubscription(currentUser.uid);
            } else {
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, [checkStripeSubscription]);

    // --- ACȚIUNI BUTOANE ---
    const handleAction = async (planKey) => {
        if (!user) {
            navigate('/signin');
            return;
        }

        // 1. LOGICA PENTRU TRANSFORMARE (Formular)
        if (planKey === 'transform') {
            window.open(TRANSFORM_FORM_URL, '_blank');
            return;
        }

        // 2. LOGICA PENTRU STARTER & PRO (Plată Stripe)
        const priceId = STRIPE_PRICES[planKey];
        if (!priceId || priceId.includes("PUNE_AICI")) {
            return alert("Eroare configurare preț. Contactează suportul.");
        }

        try {
            const checkoutRef = collection(db, "customers", user.uid, "checkout_sessions");
            const docRef = await addDoc(checkoutRef, {
                price: priceId,
                success_url: window.location.origin + "/plans",
                cancel_url: window.location.origin + "/plans",
            });

            onSnapshot(docRef, (snap) => {
                const {error, url} = snap.data();
                if (error) alert(`Eroare: ${error.message}`);
                if (url) window.location.assign(url);
            });
        } catch (e) {
            console.error(e);
            alert("Eroare la inițierea plății.");
        }
    };

    const handlePortal = async () => {
        if (!user) return alert("Nu ești logat.");
        if (userData?.subscriptionPlan === 'transform') {
            alert("Pentru modificări la planul Transformare, te rugăm să contactezi direct antrenorul.");
            return;
        }

        const btn = document.activeElement;
        const originalText = btn.innerText;
        btn.innerText = "Se procesează...";
        btn.disabled = true;

        try {
            const functions = getFunctions();
            const createPortalSession = httpsCallable(functions, 'createPortalSession');
            const result = await createPortalSession({
                returnUrl: window.location.origin + "/plans"
            });
            const { url } = result.data;
            if (url) window.location.href = url;
            else alert("Eroare: Nu s-a primit link-ul.");
        } catch (error) {
            console.error("Eroare:", error);
            alert("Eroare: " + error.message);
        } finally {
            if(btn) {
                btn.innerText = originalText;
                btn.disabled = false;
            }
        }
    };

    const renderPlanContent = (blocks) => {
        if (!blocks || blocks.length === 0) return <div className="empty-msg">Se încarcă planul...</div>;
        return (
            <div className="blocks-feed">
                {blocks.map((block, index) => (
                    <div key={index} className="plan-block">
                        {block.type === 'text' && <p className="block-text">{block.content}</p>}
                        {block.type === 'image' && block.url && <div className="block-media"><img src={block.url} alt="" /></div>}
                        {block.type === 'video' && block.url && <div className="block-media"><video src={block.url} controls controlsList="nodownload" /></div>}
                        {block.type === 'youtube' && block.url && <div className="block-media"><YouTubeEmbed url={block.url} /></div>}
                    </div>
                ))}
            </div>
        );
    };

    if (loading) return <div className="loading-screen">Se încarcă...</div>;

    // --- UI: ABONAMENT ACTIV ---
    if (hasActiveSub) {
        const planName = userData?.subscriptionPlan && PLAN_NAMES[userData.subscriptionPlan]
            ? PLAN_NAMES[userData.subscriptionPlan]
            : "Abonament Activ";

        let expiryDateInfo = null;
        if (userData?.subscriptionPlan === 'transform' && userData?.expiresAt) {
            const expDate = userData.expiresAt.toDate ? userData.expiresAt.toDate() : new Date(userData.expiresAt);
            expiryDateInfo = <p style={{fontSize: '0.9rem', color: '#ff4444', marginTop: '5px'}}>Expiră la: {expDate.toLocaleDateString('ro-RO')}</p>;
        }

        return (
            <div className="my-plan-container">
                <div className="plan-header">
                    <div className="status-badge-user active">STATUS: ACTIV ✅</div>
                    <h1>{planName}</h1>
                    <p>Salut, <span style={{color: '#ccff00'}}>{userData?.fullName}</span>!</p>
                    {expiryDateInfo}
                    <button onClick={handlePortal} className="manage-btn">
                        ⚙️ Gestionează Abonamentul
                    </button>
                </div>

                <div className="plan-content-grid">
                    <div className="active-plan-card diet-card">
                        <div className="card-header"><h2>🥦 Plan Alimentar</h2></div>
                        {renderPlanContent(dietBlocks)}
                    </div>
                    <div className="active-plan-card workout-card">
                        <div className="card-header"><h2>💪 Antrenament</h2></div>
                        {renderPlanContent(workoutBlocks)}
                    </div>
                </div>
            </div>
        );
    }

    // --- UI: LISTA DE PREȚURI ---
    return (
        <div className="pricing-container">
            <div className="pricing-header">
                <h2>Alege-ți Nivelul de Disciplină</h2>
                <p>Status curent: <span style={{color:'red'}}>Inactiv</span></p>
            </div>

            <div className="pricing-grid">

                {/* 1. PLANUL STARTER */}
                <div className="plan-card">
                    <div className="card-top">
                        <h3>🟢 Fit Simplu Starter</h3>
                        <p className="subtitle">Primul pas către disciplină.</p>
                        <div className="price-tag">700 Lei <span>/ lună</span></div>
                    </div>

                    <ul className="features-list">
                        <li>✅ Strategie alimentară clară</li>
                        <li>✅ Program antrenament profesional</li>
                        <li>✅ Acces platformă online</li>
                        <li>✅ Ghidare simplă (fără monitorizare)</li>
                    </ul>

                    <div className="card-footer">
                        <p className="target-audience">Ideal pentru: Persoane motivate care vor ordine și structură.</p>
                        <button className="neon-btn" onClick={() => handleAction('starter')}>
                            Alege Starter
                        </button>
                    </div>
                </div>

                {/* 2. PLANUL PRO */}
                <div className="plan-card featured">
                    <div className="ribbon">Recomandat</div>
                    <div className="card-top">
                        <h3>🔵 Fit Simplu Pro ⭐</h3>
                        <p className="subtitle">Suport complet pentru progres.</p>
                        <div className="price-tag">900 Lei <span>/ lună</span></div>
                    </div>

                    <ul className="features-list">
                        <li>✅ <strong>Alimentație 100% personalizată</strong></li>
                        <li>✅ <strong>Antrenament personalizat</strong></li>
                        <li>✅ Ajustări regulate & Progres</li>
                        <li>✅ Monitorizare & Feedback direct</li>
                    </ul>

                    <div className="card-footer">
                        <p className="target-audience">Ideal pentru: Cei care vor rezultate reale și consecvente.</p>
                        <button className="neon-btn" onClick={() => handleAction('pro')}>
                            Alege Pro Lunar
                        </button>
                    </div>
                </div>

                {/* 3. PLANUL TRANSFORMARE */}
                <div className="plan-card transformation-card">
                    <div className="card-top">
                        <h3>🔴 Fit Simplu Transformation</h3>
                        <p className="subtitle">Transformare fizică și mentală.</p>
                        <div className="price-tag">12 Săptămâni</div>
                    </div>

                    <ul className="features-list">
                        <li>🔥 Strategie pe 12 săptămâni</li>
                        <li>🔥 Alimentație structurată pe etape</li>
                        <li>🔥 Antrenament progresiv</li>
                        <li>🔥 <strong>Suport Prioritar & Ajustări Săptămânale</strong></li>
                    </ul>

                    <div className="warning-box">
                        <p><strong>NU este pentru:</strong> Cei care caută soluții rapide sau le lipsește disciplina.</p>
                    </div>

                    <div className="card-footer">
                        <p className="target-audience">Acces doar prin aplicare și selecție.</p>
                        <button className="neon-btn red-btn" onClick={() => handleAction('transform')}>
                            Aplică Acum
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default Abonamente;